import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export interface UniversityApplicationsDatum {
  name: string;
  applications: number;
  success: number;
}

const FONT_SIZE = 12;
const LINE_HEIGHT = 16;
const Y_AXIS_WIDTH = 44;
const MARGIN = { top: 8, right: 8, bottom: 8, left: 8 };
const LABEL_GUTTER = 10;

/** Wrap by words where possible, splitting long tokens without dropping characters. */
export function wrapUniversityLabel(
  name: string,
  maxWidth: number,
  measure: (text: string) => number,
): string[] {
  const lines: string[] = [];
  let line = "";
  for (const word of name.trim().split(/\s+/)) {
    if (line && measure(`${line} ${word}`) <= maxWidth) {
      line += ` ${word}`;
      continue;
    }
    if (line) lines.push(line);
    line = "";
    for (const character of Array.from(word)) {
      if (line && measure(line + character) > maxWidth) {
        lines.push(line);
        line = "";
      }
      line += character;
    }
  }
  if (line) lines.push(line);
  return lines.length ? lines : [""];
}

/** Presentation only: each dashboard retains its existing fetching/counting/scope. */
export const UniversityApplicationsBars: React.FC<{
  data: UniversityApplicationsDatum[];
}> = ({ data }) => {
  const host = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [font, setFont] = useState({ family: "sans-serif", revision: 0 });

  useEffect(() => {
    let active = true;
    const refreshFont = () => {
      if (!active || !host.current) return;
      const family =
        window.getComputedStyle(host.current).fontFamily || "sans-serif";
      setFont((previous) => ({ family, revision: previous.revision + 1 }));
    };
    refreshFont();
    document.fonts?.ready.then(refreshFont);
    document.fonts?.addEventListener("loadingdone", refreshFont);
    return () => {
      active = false;
      document.fonts?.removeEventListener("loadingdone", refreshFont);
    };
  }, []);

  const chartWidth = width || 320;
  // Five narrow category columns fragment ordinary words. Give names a wider
  // left-hand column until the card can comfortably fit the vertical layout.
  const horizontal = chartWidth < 520;
  const categoryAxisWidth = Math.min(180, Math.floor(chartWidth * 0.52));

  const labelLines = useMemo(() => {
    const context = document.createElement("canvas").getContext("2d");
    if (context) context.font = `400 ${FONT_SIZE}px ${font.family}`;
    // The fallback is conservative and is only needed when canvas is unavailable.
    const measure = (text: string) =>
      context?.measureText(text).width ?? Array.from(text).length * FONT_SIZE;
    const plotWidth = Math.max(
      1,
      chartWidth - Y_AXIS_WIDTH - MARGIN.left - MARGIN.right,
    );
    const labelWidth = Math.max(
      1,
      horizontal
        ? categoryAxisWidth - LABEL_GUTTER
        : plotWidth / Math.max(1, data.length) - LABEL_GUTTER,
    );
    return data.map(({ name }) =>
      wrapUniversityLabel(name, labelWidth, measure),
    );
  }, [data, chartWidth, horizontal, categoryAxisWidth, font]);

  const maxLines = Math.max(1, ...labelLines.map((lines) => lines.length));
  const axisHeight = 16 + maxLines * LINE_HEIGHT;
  // Reserve a complete row per institution in narrow cards; keep 190px of
  // bar/grid area above the labels in the wide-card layout.
  const chartHeight = horizontal
    ? Math.max(
        250,
        data.length * (maxLines * LINE_HEIGHT + 20) +
          30 +
          MARGIN.top +
          MARGIN.bottom,
      )
    : Math.max(250, 190 + axisHeight + MARGIN.top + MARGIN.bottom);
  const categoryTick = ({ x, y, index, payload }: any) => {
    const lines = labelLines[index] || [String(payload.value)];
    return (
      <text
        x={x}
        y={y}
        fill="#666"
        textAnchor={horizontal ? "end" : "middle"}
        fontSize={FONT_SIZE}
        fontFamily={font.family}
        fontWeight={400}
        letterSpacing={0}
        aria-label={String(payload.value)}
        className="university-applications-label"
      >
        {lines.map((line, lineIndex) => (
          <tspan
            key={lineIndex}
            x={x}
            dy={
              lineIndex === 0
                ? horizontal
                  ? FONT_SIZE / 3 - ((lines.length - 1) * LINE_HEIGHT) / 2
                  : FONT_SIZE
                : LINE_HEIGHT
            }
          >
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  return (
    <div
      ref={host}
      className="university-applications-bars"
      data-layout={horizontal ? "horizontal-bars" : "vertical-bars"}
      style={{ minWidth: 0 }}
    >
      <ResponsiveContainer
        width="100%"
        height={chartHeight}
        minWidth={0}
        onResize={(nextWidth) =>
          setWidth((current) => (current === nextWidth ? current : nextWidth))
        }
      >
        <BarChart
          data={data}
          margin={MARGIN}
          layout={horizontal ? "vertical" : "horizontal"}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          {horizontal ? (
            <XAxis
              type="number"
              stroke="#666"
              allowDecimals={false}
              height={30}
            />
          ) : (
            <XAxis
              dataKey="name"
              stroke="#666"
              interval={0}
              height={axisHeight}
              tick={categoryTick}
            />
          )}
          {horizontal ? (
            <YAxis
              type="category"
              dataKey="name"
              stroke="#666"
              interval={0}
              width={categoryAxisWidth}
              tick={categoryTick}
              tickSize={0}
              tickMargin={8}
            />
          ) : (
            <YAxis stroke="#666" width={Y_AXIS_WIDTH} allowDecimals={false} />
          )}
          <Tooltip
            position={{ x: MARGIN.left, y: MARGIN.top }}
            wrapperStyle={{
              maxWidth: Math.max(
                1,
                (width || 320) - MARGIN.left - MARGIN.right,
              ),
            }}
            contentStyle={{
              backgroundColor: "#fff",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              maxWidth: 280,
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            }}
            formatter={(value: number, name: string) => [
              value,
              name === "applications"
                ? "Total Applications"
                : "Successful Applications",
            ]}
          />
          <Bar
            dataKey="applications"
            fill="#000046"
            radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
          />
          <Bar
            dataKey="success"
            fill="#059669"
            radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
